import { useEffect, useState } from "react";
import { Sparkles, ArrowRight, Check, Timer, Flame } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useCurrency } from "../../contexts/CurrencyContext";
import { useLiveBuyerCount } from "../../lib/liveBuyerCount";

function useEndOfDayCountdown() {
  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const diff = Math.max(0, end.getTime() - now.getTime());
      const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
      setRemaining(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return remaining;
}

export default function ComboBundles({ products, meta, networkName, onSelect }) {
  const { t, field, language } = useLanguage();
  const { format } = useCurrency();
  const countdown = useEndOfDayCountdown();

  if (!products || products.length === 0) return null;
  const sorted = products.slice().sort((a, b) => a.price - b.price);
  const midPack = sorted[Math.floor(sorted.length / 2)] || sorted[0];
  const serviceNames = (field(midPack, "description") || "").split(" + ").map((s) => s.replace(/^[\d\s.,]+/, "").trim()).filter(Boolean);
  const joinedServices = serviceNames.length > 1
    ? `${serviceNames.slice(0, -1).join(", ")} ${language === "en" ? "and" : "et"} ${serviceNames[serviceNames.length - 1]}`
    : (serviceNames[0] || "");
  const bestDiscountPct = Math.round(Math.max(...sorted.map((p) => (p.compare_at_price ? (1 - p.price / p.compare_at_price) * 100 : 0))));
  const comboSubtitle = language === "en"
    ? `${joinedServices} bundled together, up to ${bestDiscountPct}% cheaper than buying separately.`
    : `${joinedServices} réunis dans un seul pack, jusqu'à ${bestDiscountPct}% moins cher qu'à l'unité.`;

  return (
    <div className="max-w-5xl mx-auto mb-20" data-testid="combo-bundles-section">
      <div className="text-center mb-8">
        <span
          className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full text-white mb-4"
          style={{ background: `linear-gradient(135deg, ${meta.solid}, #a855f7)` }}
        >
          <Sparkles className="w-3.5 h-3.5" /> {t("network.combo_badge", "Offre groupée")}
        </span>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2">
          {t("network.combo_title", "Pack complet")} {networkName}
        </h2>
        <p className="text-sm text-zinc-500 max-w-xl mx-auto">
          {comboSubtitle}
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-5">
        {sorted.map((p) => (
          <ComboCard key={p.id} p={p} meta={meta} t={t} field={field} format={format} countdown={countdown} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

function ComboCard({ p, meta, t, field, format, countdown, onSelect }) {
  const liveBuyers = useLiveBuyerCount(p.id);
  const savings = p.compare_at_price ? Math.round(p.compare_at_price - p.price) : 0;
  const savingsPct = p.compare_at_price ? Math.round(((p.compare_at_price - p.price) / p.compare_at_price) * 100) : 0;
  const items = (field(p, "description") || "").split(" + ");
  return (
    <div
      className={`relative rounded-2xl p-6 flex flex-col ${p.popular ? "border-2" : "border border-white/10"}`}
      style={p.popular ? { borderColor: meta.solid, background: `linear-gradient(160deg, ${meta.solid}14, transparent)` } : { background: "rgba(255,255,255,0.03)" }}
      data-testid={`combo-card-${p.id}`}
    >
      {p.popular && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold px-3 py-1 rounded-full text-white" style={{ background: `linear-gradient(135deg, ${meta.solid}, #a855f7)` }}>
          {t("network.combo_popular", "Le plus choisi")}
        </span>
      )}
      <h3 className="font-heading text-lg font-bold mb-1 mt-2">{field(p, "name")}</h3>
      {p.popular && (
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-400 mb-2" data-testid="combo-limited-offer-countdown">
          <Timer className="w-3.5 h-3.5" /> {t("network.combo_limited_offer", "Offre limitée, se termine dans")} <span className="tabular-nums">{countdown}</span>
        </div>
      )}
      {p.popular && (
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-400 mb-2" data-testid={`combo-live-buyers-${p.id}`}>
          <Flame className="w-3.5 h-3.5" /> {liveBuyers} {t("network.live_buyers_now", "personnes achètent ce pack en ce moment")}
        </div>
      )}
      <ul className="space-y-2 my-4 flex-1">
        {items.map((it) => (
          <li key={it} className="flex items-center gap-2 text-sm text-zinc-400">
            <Check className="w-4 h-4 shrink-0" style={{ color: meta.solid }} /> {it}
          </li>
        ))}
      </ul>
      <div className="flex items-baseline gap-2 mb-1">
        <span className="text-2xl font-heading font-extrabold">{format(p.price)}</span>
        {p.compare_at_price && <span className="text-sm text-zinc-500 line-through">{format(p.compare_at_price)}</span>}
      </div>
      {savings > 0 && (
        <p className="text-xs font-semibold text-emerald-400 mb-4" data-testid={`combo-savings-${p.id}`}>
          {t("network.combo_savings", "Économisez")} {format(savings)} (-{savingsPct}%)
        </p>
      )}
      <button
        onClick={() => onSelect(p)}
        className="w-full rounded-full py-2.5 text-sm font-bold text-white flex items-center justify-center gap-1.5 transition-transform hover:-translate-y-0.5"
        style={{ background: `linear-gradient(135deg, ${meta.solid}, #a855f7)` }}
        data-testid={`combo-select-btn-${p.id}`}
      >
        {t("network.combo_choose", "Choisir ce pack")} <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
